import {useCallback, useEffect, useState} from "react";
import {
  approveWorkPackage,
  listWorkPackageApprovals,
  rejectWorkPackage,
  returnWorkPackageForRevision,
  type WorkPackageApproval
} from "../api/client";
import {useAuth} from "../auth/AuthContext";
import {PageHeader} from "../components/PageHeader";

type Decision = "approve" | "reject" | "return";

export function ApprovalInbox() {
  const {authorizedFetch, user} = useAuth();
  const [items, setItems] = useState<WorkPackageApproval[]>([]);
  const [selected, setSelected] = useState<WorkPackageApproval | null>(null);
  const [decision, setDecision] = useState<Decision>("approve");
  const [rationale, setRationale] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const refresh = useCallback(async (signal?: AbortSignal) => {
    const result = await listWorkPackageApprovals(authorizedFetch, signal);
    setItems(Array.isArray(result) ? result : []);
  }, [authorizedFetch]);

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal).catch(problem => {
      if (!controller.signal.aborted) setError(message(problem));
    });
    return () => controller.abort();
  }, [refresh]);

  const begin = (item: WorkPackageApproval, next: Decision) => {
    setSelected(item);
    setDecision(next);
    setRationale("");
    setError("");
    setNotice("");
  };

  const decide = async () => {
    if (!selected || !rationale.trim()) {
      setError(decision === "approve"
        ? "An approval rationale is required."
        : decision === "reject"
          ? "A rejection reason is required."
          : "A return-for-revision reason is required.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (decision === "approve") {
        await approveWorkPackage(authorizedFetch, selected, rationale.trim());
        setNotice(`${selected.key} v${selected.exactVersion} was approved and activated.`);
      } else if (decision === "reject") {
        await rejectWorkPackage(authorizedFetch, selected, rationale.trim());
        setNotice(`${selected.key} v${selected.exactVersion} was rejected.`);
      } else {
        await returnWorkPackageForRevision(
          authorizedFetch, selected, rationale.trim()
        );
        setNotice(`${selected.key} v${selected.exactVersion} was returned; its immutable submitted version was preserved and Draft v${selected.exactVersion + 1} is ready for revision.`);
      }
      setSelected(null);
      setRationale("");
      await refresh();
    } catch (problem) {
      setError(message(problem));
    } finally {
      setBusy(false);
    }
  };

  return <>
    <PageHeader eyebrow="Human authority" title="Approval inbox"
      description="Review the exact immutable version submitted to your named Keycloak identity. Role membership alone never grants decision authority." />
    <section className="panel page-panel approval-inbox">
      <div className="boundary-banner" role="note">
        <strong>Exact named-human gate</strong>
        <span>Signed in as {user?.username}. Self-approval and external mutations remain prohibited.</span>
      </div>
      {error && <p className="form-message error" role="alert">{error}</p>}
      {notice && <p className="form-message success" role="status">{notice}</p>}
      {items.length === 0
        ? <div className="empty-state"><h2>No authored packages are pending</h2>
          <p>Only submitted versions in your tenant, company, and business unit appear here.</p></div>
        : <div className="approval-list">{items.map(item =>
          <ApprovalCard key={item.approvalId} item={item}
            onApprove={() => begin(item, "approve")}
            onReject={() => begin(item, "reject")}
            onReturn={() => begin(item, "return")} />)}</div>}
      {selected && <section className="decision-form" aria-labelledby="decision-heading">
        <h2 id="decision-heading">{decision === "approve" ? "Approve and activate" : decision === "reject" ? "Reject" : "Return for revision"} {selected.key} v{selected.exactVersion}</h2>
        <p>This decision is bound to <code>{selected.submittedVersionHash}</code>.</p>
        <label>{decision === "approve" ? "Approval rationale" : decision === "reject" ? "Rejection reason" : "Return reason"}
          <textarea required rows={4} maxLength={500} value={rationale}
            onChange={event => setRationale(event.target.value)} /></label>
        <div className="form-actions">
          <button type="button" disabled={busy} onClick={() => setSelected(null)}>Cancel</button>
          <button type="button" disabled={busy || !rationale.trim()}
            className={decision === "approve" ? "primary-action" : decision === "reject" ? "danger-action" : undefined}
            onClick={() => void decide()}>{busy ? "Recording…" : decision === "approve" ? "Approve and activate" : decision === "reject" ? "Reject exact version" : "Return exact version for revision"}</button>
        </div>
      </section>}
      <section className="fixture-card" aria-labelledby="fixture-heading">
        <span className="badge">Demonstration fixture · non-actionable</span>
        <h2 id="fixture-heading">WP-OPS-014 · Runner observability baseline</h2>
        <p>This seeded record is retained for demonstration and is not an authored-package approval.</p>
      </section>
    </section>
  </>;
}

function ApprovalCard({item, onApprove, onReject, onReturn}: {
  item: WorkPackageApproval;
  onApprove(): void;
  onReject(): void;
  onReturn(): void;
}) {
  return <article className="approval-card authored-approval" id={`approval-${item.workPackageId}`}>
    <header><div><span className="badge amber">Awaiting exact review</span>
      <h2>{item.key} · {item.title}</h2></div><strong>v{item.exactVersion}</strong></header>
    <dl className="detail-list approval-details">
      <div><dt>Submitted version hash</dt><dd><code>{item.submittedVersionHash}</code></dd></div>
      <div><dt>Submitter</dt><dd>{item.submitter}</dd></div>
      <div><dt>Named approver</dt><dd>{item.namedApprover}</dd></div>
      <div><dt>Owner organization</dt><dd>{item.ownerOrganization}</dd></div>
      <div><dt>Risk / priority</dt><dd>{item.risk} / {item.priority}</dd></div>
      <div><dt>Validation</dt><dd>{item.validationStatus.replaceAll("_", " ")}</dd></div>
      <div><dt>Submitted</dt><dd><time dateTime={item.submittedAt}>{new Date(item.submittedAt).toLocaleString()}</time></dd></div>
      <div><dt>Expires</dt><dd><time dateTime={item.expiresAt}>{new Date(item.expiresAt).toLocaleString()}</time></dd></div>
    </dl>
    <Listed title="Acceptance gates" items={item.acceptanceGates} />
    <Listed title="Evidence requirements" items={item.evidenceRequirements} />
    {item.canDecide
      ? <div className="form-actions"><button className="primary-action" type="button" onClick={onApprove}>Approve and activate</button>
        <button className="danger-action" type="button" onClick={onReject}>Reject</button>
        <button type="button" onClick={onReturn}>Return for revision</button></div>
      : <p className="readonly-note">Decision actions are available only to the exact named human approver.</p>}
  </article>;
}

function Listed({title, items}: {title: string; items: string[]}) {
  return <section><h3>{title}</h3><ul>{items.map(item => <li key={item}>{item}</li>)}</ul></section>;
}

function message(problem: unknown): string {
  return problem instanceof Error ? problem.message : "The approval operation failed";
}
